import React from "react";

import { faAngleRight, faCircleCheck } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard, Icon, Text } from "@performyard/py-components";

type ProviderConnectionCardProps = {
	/** Provider identifier — used for data-testid scoping (e.g., "zoom-connect-button"). */
	provider: string;
	/** Display name shown on the card (e.g., "Zoom", "Microsoft Teams", "Google Calendar"). */
	displayName: string;
	/** Provider icon, rendered in a 40x40 box. */
	icon: React.ReactNode;
	isConnected: boolean;
	/** Subtitle shown under the title in the connected state (e.g., email, calendar name). */
	connectedAs?: string | null;
	/** Optional inline suffix appended to the connected-state subtitle (e.g., "(Primary)" for calendar). */
	connectedSuffix?: React.ReactNode;
	/** Optional secondary action shown alongside Disconnect when connected (e.g., "Manage" for calendar). */
	secondaryAction?: {
		label: string;
		onClick: () => void;
	};
	isDisabled?: boolean;
	onConnect: () => void;
	onDisconnect: () => void;
	/** Optional content rendered below the main row, inside the card border (e.g. a status banner). */
	footer?: React.ReactNode;
};

/** Generic provider connection card used by both calendar and video-conferencing sections. */
export const ProviderConnectionCard = ({
	provider,
	displayName,
	icon,
	isConnected,
	connectedAs,
	connectedSuffix,
	secondaryAction,
	isDisabled = false,
	onConnect,
	onDisconnect,
	footer,
}: ProviderConnectionCardProps): React.ReactElement => {
	// Main row: h-[48px] content + 16px padding top/bottom = 80px card (preflight off → content-box).
	// A footer, when present, stacks below the row inside the same border.
	return (
		<div className="flex flex-col gap-4 rounded-lg p-4 outline outline-1 outline-neutral-2">
			<div className="flex h-[48px] w-full items-center justify-between gap-2">
				<div className="flex items-center gap-4">
					<div className="flex h-[40px] w-[40px] items-center justify-center">{icon}</div>

					<div className="flex flex-col gap-1">
						<Text size="paragraph-3" weight="semi-bold">
							{displayName}
						</Text>
						{isConnected ? (
							<div className="flex flex-row items-center">
								<Icon icon={faCircleCheck} size={16} color="green-7" />
								<Text size="caption" weight="regular" color="green-7">
									{connectedAs || "Connected"}
									{connectedSuffix}
								</Text>
							</div>
						) : (
							<Text size="caption" weight="regular" color="neutral-5">
								Not Connected
							</Text>
						)}
					</div>
				</div>

				{isConnected ? (
					<div className="flex flex-row items-center gap-2">
						<ButtonStandard
							size="small"
							color="red"
							variant="text"
							onClick={onDisconnect}
							data-testid={`${provider}-disconnect-button`}
						>
							Disconnect
						</ButtonStandard>
						{secondaryAction && (
							<ButtonStandard
								size="small"
								color="neutral"
								variant="text"
								onClick={secondaryAction.onClick}
								data-testid={`${provider}-${secondaryAction.label.toLowerCase()}-button`}
							>
								{secondaryAction.label}
							</ButtonStandard>
						)}
					</div>
				) : (
					<div className="flex justify-end">
						<ButtonStandard
							size="small"
							iconRight={faAngleRight}
							color="primary"
							variant="text"
							disabled={isDisabled}
							onClick={onConnect}
							data-testid={`${provider}-connect-button`}
						>
							Connect
						</ButtonStandard>
					</div>
				)}
			</div>
			{footer}
		</div>
	);
};
